/**
 * Shared user-message construction for itinerary generation prompts.
 *
 * Extracted from web's WanderAppComplete.jsx so that both web and mobile
 * produce byte-identical LLM prompts.  Two public functions:
 *
 *   buildFirstBatchUserMessage(params)  — Days 1-N batch prompt
 *   buildPerDayUserMessage(params)      — single-day prompt (per-day generation)
 *
 * All heavy prompt text is copied VERBATIM from the web source to guarantee
 * output parity.  Do NOT rephrase, reformat, or "improve" any template literal.
 */

import { isVagueLocation, buildTravelDayInstructions } from './promptBuilder.js';
import { buildFeedbackContext } from './profileContext.js';
// Direct imports — the TDZ issue was with barrel re-exports, not direct file imports.
import { calculateFlightArrival } from './timezoneConstants.js';
import { FLIGHT_TIMES, HOME_CITY_MAP } from './flightEstimates.js';
import { getRegionalMealTimes } from './itineraryValidation.js';
import { extractLocationFromHotelName } from './destinationConstants.js';
// NOTE: calculateFlightArrival, FLIGHT_TIMES, HOME_CITY_MAP are loaded lazily
// inside buildPerDayUserMessage() via require() to avoid barrel-export TDZ errors.
// (Same pattern as displayGuardrails.js — see index.js barrel note.)

// ─── Helpers ────────────────────────────────────────────────────────────────

const ACTIVITY_KEYWORDS = [
  'skiing', 'ski', 'safari', 'beach', 'wine', 'hiking', 'diving', 'surfing',
  'trekking', 'countryside', 'coast', 'lake district', 'jungle', 'desert', 'volcano',
  'national park', 'ruins', 'temples', 'islands', 'mountains', 'snorkeling', 'kayaking',
];

/**
 * Build a "LOCKED VALUES" preamble from the skeleton for a given day range.
 *
 * Injected into per-day prompts as B-medium variant (proven 0% drift in
 * preflight Test 2a). Empty string when skeleton is not provided — keeps
 * existing behavior unchanged for fallback path.
 *
 * @param {object|null} skeleton  - { dayPlan, transits } from services/skeleton
 * @param {number}      startDay  - inclusive
 * @param {number}      endDay    - inclusive
 * @returns {string}
 */
function buildSkeletonLockText(skeleton, startDay, endDay) {
  if (!skeleton?.dayPlan?.length) return '';

  const days = skeleton.dayPlan.filter(d => d.day >= startDay && d.day <= endDay);
  if (!days.length) return '';

  const lines = ['', '═══ LOCKED VALUES (do not change) ═══'];
  for (const d of days) {
    const transitsForDay = (skeleton.transits || []).filter(t => t.day === d.day);
    const transitText = transitsForDay
      .map(t => `transit: ${t.mode} from ${t.from} to ${t.to}, ${t.duration || `${t.durationMinutes}min`}${t.departureTime ? ` (${t.departureTime}-${t.arrivalTime})` : ''}`)
      .join('; ');
    lines.push(
      `Day ${d.day}: baseCity="${d.baseCity}"${d.country ? `, country="${d.country}"` : ''}${transitText ? ` | ${transitText}` : ''}`
    );
  }
  lines.push('Use these exact baseCity values for each day. Plan activities ONLY in the assigned baseCity.');
  lines.push('Do NOT invent inter-city transits — use the locked transit values above.');
  lines.push('═══════════════════════════════════════');

  // Append constraints block as STRICT RULES the LLM must follow. Computed
  // by services/skeleton/constraints.js based on companions + universal
  // policies (sunset window, hotel check-in, no same-minute conflicts).
  // Validator backstops via postGenValidator.js.
  const constraintText = formatConstraintsBlock(skeleton.constraints);
  if (constraintText) lines.push(constraintText);

  lines.push('');
  return lines.join('\n');
}

/**
 * Inline renderer for skeleton constraints. Mirrors
 * services/skeleton/constraints.js#formatConstraintsForPrompt; inlined
 * here because userMessageBuilder.js lives in packages/shared and the
 * constraints module lives in services/, which crosses the shared/ vs
 * services/ boundary that the bundler doesn't always resolve cleanly.
 *
 * KEEP IN SYNC with services/skeleton/constraints.js#formatConstraintsForPrompt.
 * PR #318 updated the constraints module to NO TIME-RANGE OVERLAPS but missed
 * this mirror — every generation kept telling the LLM the old rule.
 * Fixed 2026-06-03 after runtime verification caught the drift.
 */
function formatConstraintsBlock(constraints) {
  if (!Array.isArray(constraints) || constraints.length === 0) return '';
  const c0 = constraints[0];
  const out = [];
  out.push('');
  out.push('🛡️ STRICT CONSTRAINTS (computed by skeleton; validator enforces):');
  out.push('- HOTEL CHECK-IN: MUST be ≥ 15:00. Before 15:00, label the activity "Drop Bags at Hotel" — NEVER "Check-In".');
  out.push('- NO TIME-RANGE OVERLAPS: every activity has a duration. Two items MUST NOT have overlapping time ranges (e.g. a 09:00 + 2h activity overlaps a 10:30 item). When items are in DIFFERENT locations, leave ≥ 15 min between the end of one and the start of the next for transit. Same-venue items can be back-to-back. The validator detects range overlap, not just same-minute collisions.');
  out.push('- SUNSET ACTIVITIES: items literally named "Sunset…" or "Sunset at…" MUST be scheduled between 17:00 and 21:00. Outside this window is wrong globally.');

  if (c0.companions?.hasElderly) {
    out.push('- ELDERLY COMPANION: AVOID activities whose name or description contains: "stair", "stairs", "hilly", "hike", "hiking", "climb", "trek", "steep", "cobbleston", "miradouro", "viewpoint". If a viewpoint or hilltop is unavoidable, it MUST explicitly state an elevator/funicular/cable-car alternative.');
    out.push('- ELDERLY COMPANION: insert one rest period (cafe / sit-down break) between 14:00 and 16:00 every full day.');
  }
  if (c0.companions?.hasKids) {
    out.push('- KIDS COMPANION: max 4 named activities per day (the validator will flag a 5th).');
    out.push('- KIDS COMPANION: dinner MUST be ≤ 19:30. Target 18:00–18:30. Validator flags > 19:30.');
    out.push('- KIDS COMPANION: NO multi-course tasting-menu restaurants for lunch or dinner. NO restaurants where average service > 90 min.');
  }
  if (c0.companions?.hasElderly && c0.companions?.hasKids) {
    out.push('- COMBINED ELDERLY+KIDS: dinner cutoff tightens to ≤ 18:30. Max 4 activities. NO tasting menus, NO stairs/hills, NO long walks.');
  }
  return out.join('\n');
}

// ─── First batch ────────────────────────────────────────────────────────────

/**
 * Build the user-message string for the first-batch generation request
 * (Days 1 through batchSize).
 *
 * Produces the EXACT same prompt that web's generateFirstBatch() builds at
 * lines 2787-2943 of WanderAppComplete.jsx.
 *
 * @param {Object} params
 * @param {{ name: string, stops?: Array }} params.destination
 * @param {number} params.totalDays
 * @param {number} params.batchSize — typically Math.min(2, totalDays)
 * @param {Object} params.travelStruct — from calculateTravelStructure()
 * @param {Object} params.transportInfo — { [destName]: { carFree, carFreeNote, nearestAirport, alternateAirport } }
 * @param {Object|null} params.preFetchedTransport
 * @param {string} params.effectiveReturnMode — 'fly'|'train'|'ferry'|'custom'|'drive-own'|'drive-rental'
 * @param {string} params.transportationMode — outbound mode
 * @param {Object} params.userPrefs
 * @returns {string}
 */
export function buildFirstBatchUserMessage({
  destination,
  totalDays,
  batchSize,
  travelStruct,
  transportInfo = {},
  preFetchedTransport = null,
  effectiveReturnMode,
  transportationMode,
  userPrefs,
  skeleton = null,
}) {
  // Calculate leave-by time for departure day (3 hours before departure for international flights)
  const leaveByTime = (() => {
    if (!userPrefs.departureTime) return null;
    const [h, m] = userPrefs.departureTime.split(':').map(Number);
    const rawMin = h * 60 + m - 180; // 3 hours before departure
    const leaveMin = Math.max(0, rawMin);
    return `${String(Math.floor(leaveMin / 60)).padStart(2, '0')}:${String(leaveMin % 60).padStart(2, '0')}`;
  })();
  // For red-eye flights (departure before 3 AM), the traveler needs to leave the previous evening
  const isRedEyeDeparture = (() => {
    if (!userPrefs.departureTime) return false;
    const [h, m] = userPrefs.departureTime.split(':').map(Number);
    return (h * 60 + m - 180) < 0;
  })();

  // Detect vague/conceptual stop names that the AI needs to resolve to real places
  const activityKw = ACTIVITY_KEYWORDS;
  const vagueStops = travelStruct?.stops?.filter(s => {
    const lower = (s.resolvedLocation || s.name).toLowerCase();
    return !s.resolvedLocation && !s.name.includes(',') && activityKw.some(kw => lower.includes(kw));
  }) || [];

  let vagueLocationContext = '';
  if (vagueStops.length > 0) {
    vagueLocationContext = `
⚠️ IMPORTANT - RESOLVE VAGUE DESTINATIONS:
Some stops are conceptual/activity-based, NOT specific places. You MUST pick real locations:
${vagueStops.map(s => `- "${s.name}" → Pick a specific, well-known destination (e.g., "Japan Skiing" → Niseko/Hakuba, "African Safari" → Maasai Mara, etc.)`).join('\n')}
Use the REAL town/resort name in all "baseCity", "location", and "address" fields.
`;
  }

  // Build travel context for the prompt
  let travelContext = '';
  if (travelStruct?.isMultiStop) {
    travelContext = `
MULTI-STOP TRIP STRUCTURE:
${travelStruct.stops.map(s => `- ${s.resolvedLocation || s.name}: Days ${s.startDay}-${s.endDay}`).join('\n')}

Travel days: ${travelStruct.travelDays.map(t => `Day ${t.day} (${t.from} → ${t.to})`).join(', ') || 'None in first batch'}
`;
  }

  // Build transport context for car-free zones and special logistics
  let transportContext = '';
  const carFreeDestinations = Object.entries(transportInfo)
    .filter(([_, info]) => info?.carFree)
    .map(([name, info]) => ({
      name,
      note: info.carFreeNote,
      nearestAirport: info.nearestAirport,
      alternateAirport: info.alternateAirport
    }));

  if (carFreeDestinations.length > 0) {
    transportContext = `
🚂 TRANSPORT RESTRICTIONS:
${carFreeDestinations.map(d => `
${d.name.toUpperCase()} IS CAR-FREE:
${d.note || 'Private vehicles are not allowed.'}
- Nearest airport: ${d.nearestAirport || 'See local transport'}
${d.alternateAirport ? `- Alternate airport: ${d.alternateAirport}` : ''}
- Include realistic train/shuttle times in activity descriptions
`).join('\n')}
`;
  }

  // Get regional meal times for the primary destination
  const primaryDestination = travelStruct?.stops?.[0]?.name || destination.name;
  const regionalMealTimes = getRegionalMealTimes(primaryDestination);

  // Calculate earliest first activity time for Day 1
  const arrivalHour = parseInt((userPrefs.arrivalTime || '12:00').split(':')[0]) || 12;
  const earliestActivityHour = arrivalHour + 2;
  const earliestActivityDisplay = earliestActivityHour >= 12
    ? `${earliestActivityHour > 12 ? earliestActivityHour - 12 : 12}:00 PM`
    : `${earliestActivityHour}:00 AM`;

  const prompt = `Generate Days 1-${batchSize} of a ${totalDays}-day trip to ${destination.name}.
${vagueLocationContext}
${travelContext}
${transportContext}

SCHEDULING RULES (MUST FOLLOW):
1. No two activities at the same time unless at the same venue (sequential is OK)
2. Allow travel buffer between different locations: same area 15-20 min, different areas 30-45 min
3. Meal times for ${primaryDestination}:
   - Breakfast: ${regionalMealTimes.breakfast[0]}:00-${regionalMealTimes.breakfast[1]}:00
   - Lunch: ${regionalMealTimes.lunch[0]}:00-${regionalMealTimes.lunch[1]}:00
   - Dinner: ${regionalMealTimes.dinner[0]}:00-${regionalMealTimes.dinner[1]}:00
4. Day 1 ARRIVAL: Traveler arrives at ${userPrefs.arrivalTime || '12:00 (noon, assumed)'}. NO activities before arrival. First activity no earlier than ${earliestActivityDisplay} (allow for customs, baggage, transit to hotel, check-in).${!userPrefs.arrivalTime ? ' Arrival time not specified - first activity around 2:00 PM-3:00 PM.' : ` First activity around ${earliestActivityDisplay}.`}
5. On travel days between cities: No activities during transit. First activity 2-3 hours after arrival in new city.
6. DURATION GUIDELINES: Museums 1.5-3 hrs, restaurants 1-2 hrs, coffee stops 30 min, major attractions 2-4 hrs
6b. DAY SPAN: Regular days (not arrival/departure) should span from breakfast through dinner. Even on relaxed pace, schedule breakfast + activities + lunch + afternoon activity/free time + dinner. The LAST scheduled item each day should be dinner (typically 19:00-21:00). Do NOT end the day at 3pm with "return to hotel" — instead fill the afternoon and evening naturally.
${userPrefs.departureTime ? (effectiveReturnMode === 'fly' ? `
7. 🚨 LAST DAY (Day ${totalDays}) IS DEPARTURE DAY - CRITICAL TIMING:
   - Flight departs at ${userPrefs.departureTime} from ${userPrefs.departureAirport || 'departure airport'}${isRedEyeDeparture ? `
   - ⚠️ RED-EYE DEPARTURE: This is a red-eye flight departing before 3 AM. The traveler must leave for the airport the EVENING BEFORE (Day ${totalDays - 1}). Do NOT schedule any evening activities on Day ${totalDays - 1}. Day ${totalDays} should ONLY contain: transit to airport → departure.` : `
   - Traveler must LEAVE FOR AIRPORT by ${leaveByTime} (3 hours before departure for international flights)
   - ALL activities on Day ${totalDays} must END before ${leaveByTime}`}
   - Day ${totalDays} MUST follow this structure:
     1. ${isRedEyeDeparture ? 'Transit to departure airport (evening before)' : 'Light breakfast near hotel (early)'}
     2. Hotel Checkout
     3. Transit to departure airport
     4. Airport departure at ${userPrefs.departureTime}
   - Do NOT schedule full city tours, day trips, or activities in distant cities on departure day
   - Do NOT schedule ANY activity that starts after ${isRedEyeDeparture ? '18:00 on Day ' + (totalDays - 1) : leaveByTime}
   - Do NOT schedule activities in a DIFFERENT city from the hotel on departure day (e.g., no "Porto exploration" if staying in Ericeira — only transit through Porto to reach the airport)
   - If the departure airport is in a different city from the hotel, the ONLY reason to go to that city is to catch the flight — NOT for sightseeing
   - 🚆 AIRPORT TRANSIT MODE: ${preFetchedTransport?._departureLeg?.mode
     ? `Take the ${preFetchedTransport._departureLeg.mode === 'train' ? 'train' : preFetchedTransport._departureLeg.mode} to the airport (${preFetchedTransport._departureLeg.duration}). Use this EXACT mode — do not suggest an alternative.`
     : 'Use the most practical ground transport to reach the airport. For nearby cities (under 3 hours apart), ALWAYS prefer high-speed train over flying. Do NOT suggest a short-haul domestic flight between nearby cities. Only suggest a connecting flight if the airport is on a different island or 4+ hours away by ground.'}

   ✅ GOOD Day ${totalDays} example (${userPrefs.departureTime} flight from ${userPrefs.departureAirport || 'airport'}):
     07:00 - Breakfast at hotel
     08:00 - Hotel Checkout
     08:30 - Drive/Transit to ${userPrefs.departureAirport || 'airport'} (account for drive time!)
     ${userPrefs.departureTime} - Flight departure
     (Only 3-4 items on the departure day. Keep it SIMPLE.)

   ❌ BAD Day ${totalDays} — DO NOT DO THIS:
     08:00 - "Scenic Drive to Porto" (3.5 hours — starts before checkout!)
     09:00 - Hiking at a park (you're supposed to be driving to the airport!)
     11:00 - Hotel Checkout (too late, AFTER other activities started!)
     13:00 - Restaurant in airport city (the flight already departed at ${userPrefs.departureTime}!)
     This is WRONG because activities conflict and extend past the departure time.` : effectiveReturnMode === 'train' || effectiveReturnMode === 'ferry' ? `
7. 🚨 LAST DAY (Day ${totalDays}) IS DEPARTURE DAY - CRITICAL TIMING:
   - ${effectiveReturnMode === 'train' ? 'Train' : 'Ferry'} departs at ${userPrefs.departureTime}
   - Traveler must LEAVE FOR ${effectiveReturnMode === 'train' ? 'STATION' : 'PORT'} by ${(() => { const [h,m] = userPrefs.departureTime.split(':').map(Number); const mins = Math.max(0, h*60+m-60); return String(Math.floor(mins/60)).padStart(2,'0')+':'+String(mins%60).padStart(2,'0'); })()} (1 hour before departure)
   - Day ${totalDays} MUST follow this structure:
     1. Light breakfast near hotel (early)
     2. Hotel Checkout
     3. Transit to ${effectiveReturnMode === 'train' ? 'station' : 'port'}
     4. ${effectiveReturnMode === 'train' ? 'Train' : 'Ferry'} departure at ${userPrefs.departureTime}
   - Do NOT schedule full city tours, day trips, or activities in distant cities on departure day
   - Keep Day ${totalDays} SHORT AND SIMPLE — only 3-4 items` : effectiveReturnMode === 'custom' ? `
7. 🚨 LAST DAY (Day ${totalDays}) IS DEPARTURE DAY:
   - Departure time: ${userPrefs.departureTime}
   - Day ${totalDays} MUST follow this structure:
     1. Light breakfast near hotel
     2. Hotel Checkout
     3. Departure at ${userPrefs.departureTime}
   - Do NOT schedule full city tours or activities in distant cities on departure day
   - Keep Day ${totalDays} SHORT AND SIMPLE` : `
7. 🚨 LAST DAY (Day ${totalDays}) IS DEPARTURE DAY - CRITICAL TIMING:
   - Departure at ${userPrefs.departureTime}
   - ALL activities on Day ${totalDays} must END well before ${userPrefs.departureTime}
   - Day ${totalDays} MUST follow this structure:
     1. Light breakfast near hotel (early)
     2. Hotel Checkout
     3. Drive home starting at ${userPrefs.departureTime}
   - Do NOT schedule full city tours, day trips, or activities in distant cities on departure day
   - Keep Day ${totalDays} SHORT AND SIMPLE`) : `
7. LAST DAY (Day ${totalDays}): Departure day - include morning activities, hotel checkout, and departure. Last activity by noon if departure time not specified.`}

User preferences:
- Budget: ${userPrefs.budget || 'moderate'}
- Activity level: ${userPrefs.activityLevel || 'moderate'}
- Pace: ${userPrefs.preferredPace || 'moderate'}
- 🚨 PACE CONSTRAINT: ${userPrefs.preferredPace === 'relaxed' ? 'RELAXED — 2-3 activities per day (NOT 5-6). Include free time and slow mornings. Quality over quantity. But still fill the day until evening — add relaxed evening activities (sunset walk, night market, casual dinner stroll).' : userPrefs.preferredPace === 'packed' ? 'PACKED — 5-6 activities per day. Fill the schedule from morning to night.' : userPrefs.preferredPace === 'fast' ? 'FAST — 4-5 activities per day. Fill from morning to evening.' : 'MODERATE — 3-4 activities per day. Fill the day with activities spread from morning to evening (last activity should end no earlier than 17:00 on non-departure days).'}
- 🚨 SCHEDULE FULLNESS: Each non-departure day MUST have activities spanning from morning until at least 17:00-18:00. Do NOT end a day at 14:00-15:00 with "return to hotel" — that wastes half the day. If the pace is relaxed, fill with low-key evening activities (sunset viewing, neighborhood walk, casual dinner).
- Cuisine preferences: ${userPrefs.cuisinePreferences?.join(', ') || 'flexible'}
- Home city: ${userPrefs.homeCity || 'Not specified'}
- Arrival time: ${userPrefs.arrivalTime || '12:00 (assumed midday)'}
- Departure time: ${userPrefs.departureTime || 'Not specified'}${userPrefs.departureAirport ? ` from ${userPrefs.departureAirport}` : ''}
- 🚨 Daily start time: ${userPrefs.preferredStartTime || '09:00'} — The FIRST activity each day (including breakfast) must be at or AFTER this time. Do NOT schedule any activity before ${userPrefs.preferredStartTime || '09:00'} on regular days (Days 2 through ${totalDays - 1}).
${userPrefs._tripContextBlock || ''}
${userPrefs.interests?.length > 0 ? `\n🎯 TRIP INTERESTS (HIGH PRIORITY - these are what the user specifically asked for):\n${userPrefs.interests.map(i => `- ${i}`).join('\n')}\nSchedule activities that match these interests. At least 1-2 activities per day should relate to these interests.\n` : ''}
${userPrefs.requestedPlaces?.length > 0 ? `\n📍 SPECIFIC PLACES REQUESTED BY USER (MUST include these):\n${userPrefs.requestedPlaces.map(p => `- ${p}`).join('\n')}\nThese are specific venues/attractions the user explicitly asked for. Include them in the itinerary.\n` : ''}
${userPrefs.dreamDestinations?.length > 0 ? `\n🌟 DREAM DESTINATIONS: ${userPrefs.dreamDestinations.join(', ')}\n` : ''}${userPrefs.lovedPlaces?.length > 0 ? `\n❤️ PLACES THEY'VE LOVED: ${userPrefs.lovedPlaces.join(', ')} (use these to understand their taste)\n` : ''}${userPrefs.avoidPlaces?.length > 0 ? `\n🚫 PLACES/THINGS TO AVOID: ${userPrefs.avoidPlaces.join(', ')}\n` : ''}
${userPrefs.favoriteDestinations?.length > 0 ? `\n🌟 USER FAVORITE DESTINATIONS (incorporate if relevant to this trip):\n${userPrefs.favoriteDestinations.map(f => `- ${f}`).join('\n')}\nIf any of these match or overlap with the current destination, prioritize visiting those places and related experiences.\n` : ''}
${userPrefs.leastFavoriteDestinations?.length > 0 ? `\n👎 LEAST FAVORITE DESTINATIONS (the user does NOT enjoy these types of places):\n${userPrefs.leastFavoriteDestinations.map(f => `- ${f}`).join('\n')}\nAvoid suggesting similar experiences or destinations to these.\n` : ''}
${userPrefs.travelNotes ? `\n📝 SPECIAL REQUESTS FROM USER:\n${userPrefs.travelNotes}\nPlease honor these specific requests when planning the itinerary.\n` : ''}
${buildFeedbackContext(userPrefs)}
${userPrefs._communityContext || ''}
Return ONLY a JSON object with this structure:
{
  "destination": "${destination.name}",
  "days": [
    {
      "day": 1,
      "date": "Day 1",
      "theme": "Theme for this day — MUST fit the day's baseCity. Example themes that FIT: 'Reef Snorkeling & Beach Day' for Ambergris Caye, 'Old Quarter & Fado' for Lisbon, 'Temples & Night Market' for Chiang Mai. BAD themes (do NOT use): 'Street Food & Hidden Art' for a Caribbean island with no street-art scene, generic 'Exploration Day', or themes that describe a different city than baseCity.",
      "baseCity": "City name",
      "hotel": { "name": "Hotel Name", "address": "Full address", "pricePerNight": 150, "roomType": "Deluxe Double Room", "boardType": "Breakfast Included" },
      "schedule": [
        {
          "time": "09:00",
          "emoji": "🏛️",
          "activity": "Activity name",
          "location": "Full location with city, country",
          "address": "Full street address",
          "description": "Description with tips",
          "duration": "2 hours",
          "type": "sightseeing",
          "priceTier": "$$",
          "whatToDo": [
            { "title": "Step 1", "description": "What to do first", "duration": "30 min" },
            { "title": "Step 2", "description": "Next step", "duration": "45 min" }
          ],
          "tips": [
            { "type": "tip", "title": "Pro tip", "content": "Helpful advice", "icon": "💡" }
          ]
        }
      ],
      "meals": [
        { "time": "08:00", "emoji": "🥐", "meal": "Breakfast", "restaurant": "Name", "cuisine": "Type", "priceRange": "$$", "address": "Address", "mustTry": ["Dish 1", "Dish 2"], "specialty": "What they're known for" }
      ],
      "freeTimeSuggestions": [
        { "emoji": "☕", "activity": "Place name", "description": "Brief description" }
      ]
    }
  ]
}

CRITICAL RULES:
1. Day 1 should include arrival logistics (airport/station, hotel check-in)
2. Each day needs 3-5 activities and 3 meals (breakfast, lunch, dinner)
3a. ALL times MUST be in 24-hour format (HH:MM). Use "13:00" not "1:00", "19:00" not "7:00". Morning: "08:00"-"11:59". Afternoon: "12:00"-"17:59". Evening: "18:00"-"23:59". NEVER use bare "1:00"-"9:00" for PM times.
3. Use REAL place names with full addresses including city and country
3b. 🚨 LOCATION-DESCRIPTION INTEGRITY: Every activity's "description", "whatToDo" steps, and "tips" MUST describe the SAME city/venue named in "location" and "address". NEVER describe a previous stop or different city in the description. If activity is at "Secret Beach, San Pedro, Belize" the description must be about Belize — NOT "Stroll through historic heart of Playa del Carmen". Title, location, address, and description must all refer to the same place.
4. Keep activities geographically clustered (same neighborhood per day)
5. 🚨 NO duplicate activities, restaurants, viewpoints, or attractions across ANY days. A venue may appear on AT MOST ONE day of the itinerary. If an activity-type (e.g. "sunset viewpoint") fits multiple cities, use a DIFFERENT specific venue for each city. Re-using Miradouro da Senhora do Monte on Day 6 AND Day 9 is FORBIDDEN — pick a different Lisbon viewpoint for Day 9 (Miradouro de Santa Catarina, Miradouro de São Pedro de Alcântara, etc.). This applies even when the avoid-list below is empty.
6. EVERY activity MUST have "whatToDo" array with 2-4 specific steps
10. HOTEL CONSISTENCY: If multiple days are at the same destination, they MUST use the EXACT SAME hotel (same name, same address). The traveler checks in once and stays for their entire visit.
7. NEVER include "in-flight meals", "airline food", or meals consumed on planes - only include real restaurant meals at the destination
8. CRITICAL FOR TRANSIT CALCULATIONS:
   - "address" field MUST be a REAL, SPECIFIC street address that Google Maps can find (e.g., "1600 Pennsylvania Avenue NW, Washington DC, USA")
   - "location" field MUST include city and country (e.g., "Ritz-Carlton, Mekkah Road, Al Hada, Riyadh, Saudi Arabia")
   - NEVER use generic names like "City Viewpoint", "Local Restaurant", "Morning in X", "Cultural Morning", "Explore the City", "Restaurant In [City]", "Dinner In [City]", "Lunch In [City]" - use REAL, SPECIFIC place names (e.g., "Ithaa Undersea Restaurant" not "Local Cuisine", "Le Comptoir du Panthéon" not "Restaurant In Paris", "Banana Reef" not "Morning in Maldives")
   - For natural attractions, include the region/province (e.g., "Red Sand Dunes, Riyadh Province, Saudi Arabia")
9. EVERY activity and meal MUST have an appropriate "emoji" field. Use context-aware emojis:
   - Transport: 🚂 train, ✈️ flight, 🚕 taxi, 🚌 bus, ⛴️ ferry, 🚡 cable car
   - Activities: 🏛️ museum, ⛰️ hiking, 🏖️ beach, 🎭 show, 🛍️ shopping, 💆 spa, 🎿 ski
   - Food: 🍝 Italian, 🍣 Japanese, 🥐 French, 🍺 brewery, ☕ cafe, 🍷 wine
   - Landmarks: 🗼 tower, 🏰 castle, ⛪ church, 🌅 viewpoint
   - IMPORTANT: "Interlaken" is a CITY, not a lake - use 🚂 for train transfers, NOT 🌊
${userPrefs.departureTime ? `
11. 🚨 LAST DAY (Day ${totalDays}) SCHEDULE — MUST BE SHORT AND SIMPLE:
   - Day ${totalDays} should have AT MOST: 1 breakfast + 1 checkout + 1 transit-to-${effectiveReturnMode === 'fly' ? 'airport' : effectiveReturnMode === 'train' ? 'station' : effectiveReturnMode === 'ferry' ? 'port' : 'departure'} activity
   - NO sightseeing, tours, hikes, or restaurant meals on Day ${totalDays} (except hotel breakfast)
   - Times on Day ${totalDays} must all be BEFORE ${effectiveReturnMode === 'fly' ? (isRedEyeDeparture ? '18:00 on Day ' + (totalDays - 1) : leaveByTime) : userPrefs.departureTime}
   - The schedule must be CHRONOLOGICALLY CONSISTENT: checkout BEFORE departure, NOT after!` : ''}
`;

  // Inject skeleton locks if available — validated as B-medium variant in
  // preflight Test 2a (0% drift across 8 trip types × 4 personas).
  //
  // Multi-stop guard: skip lock injection when the input already has multiple
  // explicit stops AND no expansion was needed. The existing prompt already
  // anchors per-stop correctly, and our lock text on top of an already-92K-char
  // multi-stop prompt was pushing GLM past max_tokens budget (returned 0 chars,
  // forcing Sonnet fallback that timed out). Skeleton locks are most valuable
  // for single-country expansion (Bhutan→Paro+Thimphu), which is the case they
  // were designed for.
  const inputStops = travelStruct?.stops || [];
  const skeletonExpandedBeyondInput = (skeleton?.dayPlan?.length || 0) > 0 &&
    new Set((skeleton?.dayPlan || []).map(d => (d.baseCity || '').toLowerCase())).size > inputStops.length;
  const skeletonLocks = skeletonExpandedBeyondInput
    ? buildSkeletonLockText(skeleton, 1, batchSize)
    : '';
  return skeletonLocks ? skeletonLocks + prompt : prompt;
}

// ─── Per-day ────────────────────────────────────────────────────────────────

/**
 * Build the user-message string for a single per-day generation request.
 *
 * Produces the EXACT same prompt that web's generateSingleDayItinerary() builds
 * at lines 2016-2487 of WanderAppComplete.jsx (context construction + template).
 *
 * @param {Object} params
 * @param {number} params.dayNumber
 * @param {number} params.totalDays
 * @param {{ name: string, hotel?: { name: string, address: string } }} params.destination
 * @param {Object|null} params.stopInfo — from getStopForDay()
 * @param {Object|null} params.travelInfo — { from, to, day, arrivalTime?, isRedEye? }
 * @param {Object} params.priorContext — from buildCompressedContext()
 * @param {Object|null} params.transportInfo — per-destination transport
 * @param {Object|null} params.preFetchedTransport
 * @param {string} params.effectiveReturnMode
 * @param {string} params.transportationMode — 'fly'|'drive-own'|'drive-rental'|'train'|'ferry'|'custom'
 * @param {string} params.tripType — 'hotel' | 'destination'
 * @param {string} [params.hotelName] — for geographic constraints
 * @param {string} [params.hotelAddress] — for geographic constraints
 * @param {boolean} params.isLastDay
 * @param {string|null} params.feedback — regeneration feedback
 * @param {Object} params.userPrefs
 * @returns {string}
 */
export function buildPerDayUserMessage({
  dayNumber,
  totalDays,
  destination,
  stopInfo,
  travelInfo,
  travelStruct = null,
  priorContext,
  transportInfo = null,
  preFetchedTransport = null,
  preComputedFlights = null,
  effectiveReturnMode,
  transportationMode,
  tripType,
  hotelName: hotelNameParam,
  hotelAddress: hotelAddressParam,
  isLastDay,
  feedback = null,
  userPrefs,
  batchSize = 1,
  batchEndDay = null,
  skeleton = null,
}) {
  // Extract actual geographic location from hotel/resort names for better AI generation
  // e.g., "Aman Utah" -> "Utah", "Amangiri" -> "Utah"
  // Uses resolvedLocation from tripBuilderStops if available (from async AI lookup)
  const getLocationForGeneration = (name, stopData = null) => {
    // First check if we have a pre-resolved location from async lookup
    if (stopData?.resolvedLocation) {
      return stopData.resolvedLocation;
    }
    // Fall back to sync extraction
    const extractedLocation = extractLocationFromHotelName(name);
    // If extraction found a different location, use it; otherwise use original
    return extractedLocation !== name ? extractedLocation : name;
  };

  // Get the actual geographic location for this stop/destination
  const stopLocation = stopInfo?.name ? getLocationForGeneration(stopInfo.name, stopInfo) : null;
  const destinationLocation = getLocationForGeneration(destination.name, destination);
  // Bare-country stops ("New Zealand", "Australia") ship with primaryCityHint
  // from /api/generate/start. Use that as the effective location, otherwise
  // we end up writing literal directives like `Set "baseCity": "New Zealand"`
  // into the per-day user prompt — and that's exactly what produced "Day 5 •
  // New Zealand", "Dinner in New Zealand", "flight from New Zealand to
  // Queenstown" in TestFlight. The system prompt still has the multi-city
  // directive so the LLM can pick Wellington/Hamilton/etc. on later days.
  const primaryHintCityForLocation = stopInfo?.primaryCityHint
    ? String(stopInfo.primaryCityHint).split(',')[0].trim()
    : null;
  const effectiveLocation = primaryHintCityForLocation || stopLocation || destinationLocation;

  // Detect vague/conceptual locations that need AI interpretation
  // e.g., "Japan Skiing" → AI should pick a specific ski resort like Niseko or Hakuba
  const locationLower = effectiveLocation.toLowerCase();
  const activityKeywords = ACTIVITY_KEYWORDS;
  const isVague = !effectiveLocation.includes(',') &&
    activityKeywords.some(kw => locationLower.includes(kw));

  let vagueLocationInstructions = '';
  if (isVague) {
    vagueLocationInstructions = `
⚠️ IMPORTANT: "${effectiveLocation}" is a conceptual/activity-based destination, NOT a specific place.
YOU MUST pick a specific, real location for this:
- Use your geographic knowledge to choose the BEST specific town/resort/area for "${effectiveLocation}"
- For example: "Japan Skiing" → pick Niseko, Hakuba, Nozawa Onsen, or another real ski resort
- "African Safari" → pick Maasai Mara, Serengeti, Kruger, etc.
- "Beach vacation" → pick a specific beach town
- Set the hotel and ALL activities at/near your chosen specific location
- Use the REAL name of the specific place in all "location" and "address" fields
- Set "baseCity" to the actual town/area name (e.g., "Niseko" not "Japan Skiing")
`;
  }

  // Build the deduplication constraints
  const avoidList = priorContext.usedActivities?.length > 0
    ? `\n🚫 DO NOT SUGGEST THESE (already in itinerary):\n- Activities: ${priorContext.usedActivities.join(', ')}\n- Restaurants: ${priorContext.usedRestaurants.join(', ')}\n`
    : '';

  // Build variety hint based on what types of activities have been overused
  let varietyHint = '';
  if (priorContext.activityTypeCounts && Object.keys(priorContext.activityTypeCounts).length > 0) {
    const heavyTypes = Object.entries(priorContext.activityTypeCounts)
      .filter(([_, count]) => count >= 3)
      .map(([type]) => type);
    const prevThemes = priorContext.daySummaries?.map(s => s.theme).filter(Boolean) || [];
    if (heavyTypes.length > 0 || prevThemes.length > 0) {
      varietyHint = `
🎨 VARIETY NOTE: Mix up the activity types for a well-rounded trip.
${heavyTypes.length > 0 ? `- These activity types have been used heavily: ${heavyTypes.join(', ')}. Try to include different types today.` : ''}
${prevThemes.length > 0 ? `- Previous day themes: ${prevThemes.join(', ')}. Pick a DIFFERENT theme for today.` : ''}
${priorContext.cuisinesCovered?.length > 0 ? `- Cuisines already tried: ${priorContext.cuisinesCovered.join(', ')}. Try different cuisines today.` : ''}
`;
    }
  }

  // Build recent day trips warning (prevent consecutive border crossings)
  let recentDayTripsWarning = '';
  if (priorContext.recentDayTrips?.length > 0) {
    const yesterdayTrips = priorContext.recentDayTrips.filter(t => t.day === dayNumber - 1);
    if (yesterdayTrips.length > 0) {
      const tripLocations = yesterdayTrips.map(t => t.location || t.activity).join(', ');
      recentDayTripsWarning = `
⚠️ AVOID CONSECUTIVE LONG DAY TRIPS
Yesterday (Day ${dayNumber - 1}) included these distant excursions:
${yesterdayTrips.map(t => `- ${t.activity}${t.location ? ` (${t.location})` : ''}`).join('\n')}

DO NOT schedule another trip to the same distant location today.
- If yesterday was El Calafate/Argentina, stay local in Chile today
- If yesterday was a border crossing, do NOT cross the border again today
- Vary the experiences: alternate between local days and day-trip days
- Give the traveler rest from long drives/crossings
`;
    }
  }

  // Travel day specific instructions
  let travelInstructions = '';
  if (travelInfo) {
    // Determine likely transport mode between stops:
    // If user is driving (own car / rental), inter-city travel is by car
    // Otherwise, use pre-fetched Haiku data for the actual mode/duration (eliminates hallucination)
    const isDrivingTrip = transportationMode === 'drive-own' || transportationMode === 'drive-rental';

    // Look up pre-fetched Haiku transport data for this specific city pair
    const travelPairKey = `${travelInfo.from}|${travelInfo.to}`.toLowerCase();
    const dynamicMode = preFetchedTransport?.[travelPairKey];

    let travelActivity;
    let travelModeLabel;
    let transitHint;
    if (isDrivingTrip) {
      travelActivity = `Drive from ${travelInfo.from} → ${travelInfo.to} (scenic road trip — look up the actual driving time and mention it)`;
      travelModeLabel = 'drive';
      transitHint = 'the road';
    } else if (dynamicMode?.mode && dynamicMode?.duration) {
      // Use REAL data from Haiku — eliminates hallucination about transport mode
      const modeLabels = { train: 'Train', flight: 'Flight', car: 'Drive', ferry: 'Ferry', custom: 'Custom' };
      const modeLabel = modeLabels[dynamicMode.mode] || dynamicMode.mode;
      travelActivity = `${modeLabel} from ${travelInfo.from} → ${travelInfo.to} (${dynamicMode.duration} — ${dynamicMode.reason}). Use this EXACT mode, do not suggest an alternative.`;
      travelModeLabel = `${dynamicMode.mode} (${dynamicMode.duration})`;
      transitHint = dynamicMode.mode === 'train' ? 'the train station' : dynamicMode.mode === 'flight' ? 'the airport' : 'the station/airport';
    } else {
      // Fallback: let LLM choose (only if pre-fetch failed)
      travelActivity = `Travel from ${travelInfo.from} → ${travelInfo.to} — choose the most practical transport for this route. Prefer high-speed train for nearby cities (under 3 hours), only fly if 4+ hours by ground or different islands.`;
      travelModeLabel = 'choose the most practical option (train preferred for nearby cities)';
      transitHint = 'the station/airport';
    }

    // ── Calculate estimated arrival time for flights to determine evening schedule ──
    // Uses pre-computed flight schedules from estimateFlightSchedule() when available,
    // with fallback to the legacy FLIGHT_TIMES hub table approach.
    // Pre-computed flights give concrete departure/arrival times that eliminate LLM hallucination.

    let estimatedArrivalTime = null;
    let arrivalDayOffset = 0;
    let flightHours = null;
    let preComputedDepartureTime = null;
    const isFlightLeg = dynamicMode?.mode === 'flight' || (!dynamicMode && !isDrivingTrip);

    if (isFlightLeg) {
      // 1. Best source: pre-computed flight schedule (concrete times from estimateFlightSchedule)
      const flightKey = `${travelInfo.from}|${travelInfo.to}`.toLowerCase();
      const preComputed = preComputedFlights?.[flightKey];

      if (preComputed) {
        estimatedArrivalTime = preComputed.arrivalTime;
        arrivalDayOffset = preComputed.arrivalDayOffset || 0;
        flightHours = preComputed.durationMinutes / 60;
        preComputedDepartureTime = preComputed.departureTime;
      } else {
        // 2. Fallback: pre-fetched transport data duration
        if (dynamicMode?.durationMinutes) {
          flightHours = dynamicMode.durationMinutes / 60;
        }
        // 3. Fallback: FLIGHT_TIMES hub table (covers 120+ destinations)
        if (!flightHours) {
          const fromKey = HOME_CITY_MAP[travelInfo.from.toLowerCase()];
          const toLower = travelInfo.to.toLowerCase();
          if (fromKey && FLIGHT_TIMES[toLower]) {
            flightHours = FLIGHT_TIMES[toLower][fromKey];
          } else {
            const toKey = HOME_CITY_MAP[travelInfo.to.toLowerCase()];
            const fromLower = travelInfo.from.toLowerCase();
            if (toKey && FLIGHT_TIMES[fromLower]) {
              flightHours = FLIGHT_TIMES[fromLower][toKey];
            }
          }
        }

        if (flightHours) {
          const earlyDeparture = calculateFlightArrival('10:00', flightHours, travelInfo.from, travelInfo.to);
          const middayDeparture = calculateFlightArrival('13:00', flightHours, travelInfo.from, travelInfo.to);

          if (earlyDeparture.dayOffset > 0) {
            arrivalDayOffset = earlyDeparture.dayOffset;
            estimatedArrivalTime = earlyDeparture.localArrivalTime;
          } else if (middayDeparture.dayOffset > 0) {
            arrivalDayOffset = middayDeparture.dayOffset;
            estimatedArrivalTime = middayDeparture.localArrivalTime;
          } else {
            estimatedArrivalTime = earlyDeparture.localArrivalTime;
            arrivalDayOffset = 0;
          }
        }
      }
    }

    // Determine evening activity level based on arrival time
    const arrivalHour = estimatedArrivalTime ? parseInt(estimatedArrivalTime.split(':')[0]) : null;
    const isOvernightFlight = arrivalDayOffset > 0;
    const isVeryLateArrival = !isOvernightFlight && arrivalHour !== null && arrivalHour >= 21;
    const hasLimitedEvening = !isOvernightFlight && !isVeryLateArrival && arrivalHour !== null && arrivalHour >= 18;
    // For long-haul overnight flights, estimate how much of the day is available
    // A 12h flight needs ~2h airport buffer = depart for airport ~14h before arrival
    // Use flight duration to estimate latest reasonable "leave for airport" time
    const airportBufferHours = flightHours && flightHours >= 6 ? 3 : 2; // 3h for international, 2h domestic
    const isLongHaulOvernight = isOvernightFlight && flightHours && flightHours >= 8;

    // For travel days, we need the ARRIVAL city's hotel (user checks in there tonight)
    // The baseCity should be set to the ARRIVAL city since that's where the user ends the day
    // EXCEPTION: overnight flights — traveler doesn't arrive until the next day
    const flightDurationLabel = flightHours ? `~${Math.round(flightHours)}h` : 'long';

    const eveningSection = isOvernightFlight
      ? `
⚠️ OVERNIGHT TRAVEL — TRAVELER ARRIVES TOMORROW:
The flight from ${travelInfo.from} to ${travelInfo.to} is ${flightDurationLabel} with timezone changes. The traveler will NOT arrive in ${travelInfo.to} today.
- Do NOT schedule ANY activities in ${travelInfo.to} on this day
- The traveler's first activities in ${travelInfo.to} will be on the NEXT day`
      : isVeryLateArrival
      ? `
VERY LATE ARRIVAL IN ${travelInfo.to.toUpperCase()} (~${estimatedArrivalTime} local time):
- Only schedule hotel check-in and possibly a late dinner near the hotel
- No sightseeing — the traveler just had a long journey
- Save the exploration of ${travelInfo.to} for the next day`
      : hasLimitedEvening
      ? `
EVENING IN ${travelInfo.to.toUpperCase()} (arrival ~${estimatedArrivalTime} local time):
- Schedule 1 activity max after arrival (quick neighborhood walk near hotel)
- Include a proper dinner at a well-known restaurant in ${travelInfo.to}
- Keep it light — the traveler just arrived`
      : `
EVENING IN ${travelInfo.to.toUpperCase()} (after arrival):
- Schedule 2-3 activities after arrival (neighborhood walk, landmark visit, local market, etc.)
- Include a proper dinner at a well-known restaurant in ${travelInfo.to}
- This is the traveler's FIRST TASTE of ${travelInfo.to} - make it count!
- Suggest an iconic area to explore on foot (e.g., main square, waterfront, old town)
- Consider adding a bar/nightlife suggestion or evening viewpoint`;

    const hotelSection = isOvernightFlight
      ? `
🏨 HOTEL RULE FOR OVERNIGHT TRAVEL:
- The "hotel" in your JSON should be a hotel in ${travelInfo.from.toUpperCase()} (the traveler is departing from here and sleeping on the plane)
- The "baseCity" must be "${travelInfo.from}" (the traveler spends the day here before departure)
- The traveler checks into a ${travelInfo.to} hotel on the NEXT day`
      : `
🏨 CRITICAL HOTEL RULE FOR TRAVEL DAYS:
- The "hotel" in your JSON must be a hotel in ${travelInfo.to.toUpperCase()} (the ARRIVAL city)
- The "baseCity" must be "${travelInfo.to}" (the city where the traveler sleeps tonight)
- Do NOT use a hotel from ${travelInfo.from} — the traveler is checking OUT of there today`;

    // For overnight flights: schedule activities in departure city BEFORE heading to airport
    // Use pre-computed flight times when available for concrete scheduling constraints
    const concreteDepTimeStr = preComputedDepartureTime ? ` (flight departs ~${preComputedDepartureTime})` : '';
    const concreteArrTimeStr = estimatedArrivalTime && !isOvernightFlight ? ` (arriving ~${estimatedArrivalTime} local time)` : '';
    const leaveForAirportTime = preComputedDepartureTime ? (() => {
      const [h, m] = preComputedDepartureTime.split(':').map(Number);
      const bufferMin = flightHours && flightHours >= 6 ? 180 : 120; // 3h intl, 2h domestic
      const leaveMin = Math.max(0, h * 60 + m - bufferMin);
      return `${String(Math.floor(leaveMin / 60)).padStart(2, '0')}:${String(leaveMin % 60).padStart(2, '0')}`;
    })() : null;

    const morningSection = isOvernightFlight
      ? `
ACTIVITIES IN ${travelInfo.from.toUpperCase()} (before departure to airport)${concreteDepTimeStr}:
${leaveForAirportTime ? `- ⏰ LEAVE FOR AIRPORT BY ${leaveForAirportTime} — schedule all activities BEFORE this time` : `- The flight is ${flightDurationLabel} long — schedule activities in ${travelInfo.from} from morning until ~${airportBufferHours}h before the flight`}
- Include breakfast and any meals that fit before departure
- These should be things NOT YET DONE in ${travelInfo.from} or revisits to favorites
- Include transit to the airport as the last activity before the flight
${preComputedDepartureTime ? `- ❌ NO activities after ${leaveForAirportTime} — traveler must be at the airport` : ''}`
      : `
MORNING IN ${travelInfo.from.toUpperCase()} (before departure)${concreteDepTimeStr}:
${leaveForAirportTime && !isOvernightFlight ? `- ⏰ LEAVE FOR ${transitHint} BY ${leaveForAirportTime}` : `- Leave time for hotel checkout and transit to ${transitHint}`}
- Schedule 1-2 activities (2-3 hours worth) in ${travelInfo.from}
- A proper sit-down breakfast at a local restaurant
- These should be things NOT YET DONE in ${travelInfo.from} (quick highlights or revisits)
${preComputedDepartureTime && !isOvernightFlight ? `- ❌ NO activities after ${leaveForAirportTime} — traveler needs to be at ${transitHint}` : ''}`;

    const totalSection = isOvernightFlight
      ? `\nTOTAL: Schedule as many activities as fit in ${travelInfo.from} before the airport departure. Include breakfast + lunch if there's time. No activities in ${travelInfo.to}.`
      : isVeryLateArrival
      ? `\nTOTAL: This day should have 1-2 morning activities in ${travelInfo.from} + travel + dinner near hotel in ${travelInfo.to}`
      : `\nTOTAL: This day should have 3-5 activities + 3 meals (breakfast in ${travelInfo.from}, lunch during/after travel, dinner in ${travelInfo.to})`;

    // Build concrete flight schedule summary if available
    const flightScheduleSummary = preComputedDepartureTime && estimatedArrivalTime
      ? `\n📋 FLIGHT SCHEDULE: Departs ${travelInfo.from} ~${preComputedDepartureTime} → Arrives ${travelInfo.to} ~${estimatedArrivalTime}${arrivalDayOffset > 0 ? ' (NEXT DAY)' : ''} (${flightDurationLabel} flight)`
      : '';

    travelInstructions = `
🚨 THIS IS A TRAVEL DAY (Day ${dayNumber}) - ${travelInfo.from} → ${travelInfo.to}${isOvernightFlight ? ` (OVERNIGHT FLIGHT — ${flightDurationLabel}, arrives next day)` : ''}
${isOvernightFlight ? 'The traveler will be in ' + travelInfo.from + ' until departure. NO activities in ' + travelInfo.to + ' today.' : 'This day has TWO halves - BOTH should have meaningful content:'}
Transport mode for this leg: ${travelModeLabel}${flightScheduleSummary}
${hotelSection}
${morningSection}

TRAVEL:
- ${travelActivity}
${isDrivingTrip ? '- If the drive is scenic, mention interesting stops along the way in the description\n- Include the drive as an activity with realistic duration' : '- Do NOT include any activities during transit'}
${eveningSection}
${totalSection}
`;
  }

  // Stop-specific context - use effectiveLocation for geographic accuracy
  let stopInstructions = '';
  if (stopInfo) {
    // Include both hotel name and location for context
    const locationContext = stopLocation !== stopInfo.name
      ? `${stopInfo.name} (${stopLocation})`
      : stopInfo.name;

    // Determine the hotel name to use for this stop
    // Priority: previous day's hotel at same stop > priorContext.currentHotel > stopInfo.name
    const isMiddleDayAtStop = dayNumber > stopInfo.startDay && dayNumber < stopInfo.endDay;
    const isLastDayAtStop = dayNumber === stopInfo.endDay && !stopInfo.includesDeparture;
    const previousHotel = priorContext?.currentHotel;

    if (dayNumber === stopInfo.startDay && !stopInfo.includesArrival && !travelInfo) {
      // Only show "FIRST FULL DAY" if this is NOT a travel day.
      // Travel days get their own instructions (travelInstructions) which handle both cities.
      // Determine previous stop for explicit "NOT that city" instruction
      const stopIdx = travelStruct?.stops?.findIndex(s => s.startDay === stopInfo.startDay && s.name === stopInfo.name) ?? -1;
      const prevStopName = stopIdx > 0 && travelStruct?.stops?.[stopIdx - 1]
        ? (travelStruct.stops[stopIdx - 1].resolvedLocation || travelStruct.stops[stopIdx - 1].name)
        : null;
      const notPrevCity = prevStopName ? `\n🚨 CRITICAL: You are NOW in ${effectiveLocation.toUpperCase()}, NOT ${prevStopName.toUpperCase()}. ALL activities, restaurants, and locations must be in ${effectiveLocation}. Set "baseCity": "${effectiveLocation}" in your JSON.` : '';
      stopInstructions = `\n📍 FIRST FULL DAY IN ${effectiveLocation.toUpperCase()}\n- Staying at: ${stopInfo.name}\n- You arrived yesterday evening\n- Start with hotel breakfast\n- Full day of activities in ${effectiveLocation}${notPrevCity}\n`;
    } else if (stopInfo.includesArrival && dayNumber === 1) {
      stopInstructions = `\n📍 ARRIVAL DAY IN ${effectiveLocation.toUpperCase()}\n- Staying at: ${stopInfo.name}\n- Include arrival logistics (airport transfer, hotel check-in)\n- Lighter afternoon activities (jet lag consideration)\n`;
    } else if (stopInfo.includesDeparture && dayNumber === totalDays) {
      stopInstructions = `\n📍 DEPARTURE DAY FROM ${effectiveLocation.toUpperCase()}\n- Staying at: ${stopInfo.name}\n- Morning activities before checkout\n- Include checkout and airport transfer\n- Flight home in afternoon/evening\n`;
    } else if (isMiddleDayAtStop || isLastDayAtStop) {
      // MIDDLE/CONTINUATION DAY at the same stop - MUST keep the same hotel
      const hotelToKeep = previousHotel || stopInfo.name;
      stopInstructions = `
📍 CONTINUING STAY IN ${effectiveLocation.toUpperCase()} (Day ${dayNumber - stopInfo.startDay + 1} of ${stopInfo.days} at this stop)
🏨 CRITICAL - SAME HOTEL: You MUST use the EXACT same hotel as the previous day.
- Hotel name: "${hotelToKeep}"
- The "hotel" object in your JSON response MUST have "name": "${hotelToKeep}" (copy it exactly)
- Do NOT invent a new hotel name - the traveler is staying at the SAME place for their entire ${stopInfo.days}-day stay
- Full day of activities in ${effectiveLocation}
`;
    }
  }

  // Feedback for regeneration
  const feedbackInstructions = feedback
    ? `\n⚠️ USER FEEDBACK: ${feedback}\nGenerate a DIFFERENT version of this day incorporating this feedback.\n`
    : '';

  // Transport context for car-free zones and special logistics
  let transportInstructions = '';
  if (transportInfo) {
    if (transportInfo.carFree) {
      transportInstructions = `
🚂 TRANSPORT NOTE: ${effectiveLocation.toUpperCase()} IS A CAR-FREE ZONE
${transportInfo.carFreeNote || 'Private vehicles are not allowed.'}
- Nearest airport: ${transportInfo.nearestAirport || 'See local transport'}
${transportInfo.alternateAirport ? `- Alternate airport: ${transportInfo.alternateAirport}` : ''}
- All local transport will be by train, shuttle, walking, or cable car
- Include realistic train/shuttle times in activity descriptions when relevant
`;
    } else if (transportInfo.transportNotes) {
      transportInstructions = `\n📝 TRANSPORT NOTE: ${transportInfo.transportNotes}\n`;
    }
  }

  // Get regional meal times for this destination - use geographic location
  const regionalMealTimes = getRegionalMealTimes(effectiveLocation);

  // Calculate leave-by time for departure day (3 hours before departure for international flights)
  const leaveByTime = (() => {
    if (!isLastDay || !userPrefs.departureTime) return null;
    const [h, m] = userPrefs.departureTime.split(':').map(Number);
    const rawMin = h * 60 + m - 180; // 3 hours before departure
    const leaveMin = Math.max(0, rawMin);
    return `${String(Math.floor(leaveMin / 60)).padStart(2, '0')}:${String(leaveMin % 60).padStart(2, '0')}`;
  })();
  // For red-eye flights (departure before 3 AM), the traveler needs to leave the previous evening
  const isRedEyeDeparture = (() => {
    if (!isLastDay || !userPrefs.departureTime) return false;
    const [h, m] = userPrefs.departureTime.split(':').map(Number);
    return (h * 60 + m - 180) < 0;
  })();

  // Build prompt with geographic location for activities, but mention hotel name for accommodations
  const tripDescription = stopInfo?.name && stopLocation !== stopInfo.name
    ? `${effectiveLocation} (staying at ${stopInfo.name})`
    : effectiveLocation;

  // Build GEOGRAPHIC CONSTRAINTS - critical for hotel-first trips and regional destinations
  let geographicConstraints = '';
  // Check per-stop hotel preferences first (from stopHotels), then global preferredHotel
  const stopHotelData = stopInfo?.id && userPrefs.stopHotels?.[stopInfo.id];
  const perStopHotel = stopHotelData?.bookedHotel || stopHotelData?.preferredHotel;
  const hotelName = perStopHotel || hotelNameParam || userPrefs.preferredHotel || userPrefs.hotelName || destination?.hotel?.name;
  const hotelAddress = stopInfo?.address || hotelAddressParam || userPrefs.hotelAddress || destination?.hotel?.address;

  // Detect if hotel name suggests a resort/luxury property
  const hotelNameLower = (hotelName || '').toLowerCase();
  // C3: expanded resort-keyword list — previously Montage / Little Nell /
  // Auberge / Pendry etc. weren't detected, so hotel-first trips at those
  // properties missed the on-property-activity guardrails.
  const isResortProperty = hotelNameLower.includes('resort') || hotelNameLower.includes('spa') ||
    hotelNameLower.includes('retreat') || hotelNameLower.includes('lodge') ||
    hotelNameLower.includes('villa') || hotelNameLower.includes('palace') ||
    hotelNameLower.includes('aman') || hotelNameLower.includes('four seasons') ||
    hotelNameLower.includes('ritz') || hotelNameLower.includes('mandarin oriental') ||
    hotelNameLower.includes('six senses') || hotelNameLower.includes('one&only') ||
    hotelNameLower.includes('banyan tree') || hotelNameLower.includes('soneva') ||
    hotelNameLower.includes('vineyard') || hotelNameLower.includes('winery') ||
    hotelNameLower.includes('estate') || hotelNameLower.includes('ranch') ||
    hotelNameLower.includes('chateau') || hotelNameLower.includes('château') ||
    hotelNameLower.includes('miraval') || hotelNameLower.includes('canyon ranch') ||
    hotelNameLower.includes('singita') || hotelNameLower.includes('andbeyond') ||
    hotelNameLower.includes('&beyond') || hotelNameLower.includes('como shambhala') ||
    hotelNameLower.includes('chiva-som') || hotelNameLower.includes('kamalaya') ||
    hotelNameLower.includes('sha wellness') || hotelNameLower.includes('taj') ||
    hotelNameLower.includes('oberoi') || hotelNameLower.includes('raffles') ||
    hotelNameLower.includes('peninsula') || hotelNameLower.includes('waldorf') ||
    hotelNameLower.includes('st. regis') || hotelNameLower.includes('belmond') ||
    hotelNameLower.includes('rosewood') || hotelNameLower.includes('bulgari') ||
    // C3 additions (2026-04 QA): luxury hotel brands that emphasize on-property
    // experience (spa, pools, F&B, guided activities) and were missing above.
    hotelNameLower.includes('montage') || hotelNameLower.includes('little nell') ||
    hotelNameLower.includes('auberge') || hotelNameLower.includes('pendry') ||
    hotelNameLower.includes('1 hotel') || hotelNameLower.includes('nobu') ||
    hotelNameLower.includes('edition') || hotelNameLower.includes('soho house') ||
    hotelNameLower.includes('soho farmhouse') || hotelNameLower.includes('amangiri') ||
    hotelNameLower.includes('amanyara') || hotelNameLower.includes('inn at') ||
    hotelNameLower.includes('twin farms') || hotelNameLower.includes('blackberry farm') ||
    hotelNameLower.includes('post ranch') || hotelNameLower.includes('ventana big sur') ||
    hotelNameLower.includes('calistoga') || hotelNameLower.includes('meadowood') ||
    hotelNameLower.includes('the ranch at rock creek');

  // Detect safari context dynamically from stop/hotel name
  const contextStr = `${hotelNameLower} ${(effectiveLocation || '').toLowerCase()} ${(stopInfo?.name || '').toLowerCase()}`;
  const isSafariContext = /safari|game\s*(drive|reserve|lodge)|bush\s*(lodge|camp)|wildlife\s*(reserve|lodge)|kruger|serengeti|masai\s*mara|maasai\s*mara|ngorongoro|amboseli|tsavo|okavango|chobe|singita|andbeyond|&beyond/.test(contextStr);

  // For resort stops with resolvedLocation: constrain activities to the geographic area
  const resolvedGeo = stopInfo?.resolvedLocation;
  const isResortStop = (stopInfo?.stopType === 'hotel' || stopInfo?.stopType === 'resort') || isResortProperty;
  let resortGeoConstraint = '';
  if (isResortStop && resolvedGeo && resolvedGeo !== stopInfo?.name) {
    resortGeoConstraint = `
📍 GEOGRAPHIC AREA: ${resolvedGeo}
The property "${stopInfo?.name || hotelName}" is located in ${resolvedGeo}.
- ALL activities must be within this geographic area (${resolvedGeo})
- Do NOT suggest activities in distant cities — this is a resort/property-focused stay
- Off-property excursions should be within 1-2 hours of the property
`;
  }

  if (tripType === 'hotel' && hotelName) {
    // Hard "stay on property" mode kicks in when the stop explicitly carries
    // stayOnProperty: true (the default for hotel-mode stops set in the
    // trip builder). The Maldives bug surfaced because the previous prompt
    // told the LLM to schedule 1-2 off-property activities EVERY day —
    // for a guest at Jumeirah Maldives Olhahali Island that meant 30-min
    // boat rides to Malé for "fish market" and "national museum" cards
    // every day. Hard mode bans off-property activities entirely.
    const isHardOnProperty = stopInfo?.stayOnProperty === true || stopInfo?.stayOnProperty == null;

    geographicConstraints = `
📍 GEOGRAPHIC CONSTRAINT - HOTEL-BASED TRIP
The user chose to stay at: ${hotelName}${hotelAddress ? ` (${hotelAddress})` : ''}
They return to this hotel EVERY night.
${isResortProperty && isHardOnProperty ? `
🏨 ON-PROPERTY-ONLY MODE — RESORT/PROPERTY EXPERIENCE
The user specifically chose "${hotelName}" — they want to EXPERIENCE this property,
not the surrounding city. They expect to STAY ON THE RESORT GROUNDS for the
entire stay. USE YOUR KNOWLEDGE of what this property offers.

🚫 HARD RULES (DO NOT VIOLATE):
1. EVERY activity MUST be on the resort grounds. NO off-property activities.
   This includes mosques, fish markets, national museums, city restaurants,
   public beaches, shopping streets — anything not part of the resort.
2. EVERY meal MUST be at an on-site resort restaurant. NO outside restaurants.
   If you don't know the on-site restaurant names, use generic placeholders
   like "Pool Restaurant" or "Beachside Grill" (the meal-replacer pass
   resolves these to real venues later). DO NOT pick a real off-property
   restaurant.
3. Inter-stop transit is fine (e.g. ferry/seaplane to the next resort).
   But within a single day's stay, the user does not leave the resort.
4. If running out of distinct activities, schedule unstructured FREE TIME
   (pool day, beach day, room service, in-villa spa). NEVER fill the gap
   with an off-property suggestion.

PER-DAY ACTIVITY MIX (4-6 items per day):
- 1-2 wellness/active items: spa treatment, yoga, fitness center, snorkeling,
  kayaking, tennis, golf
- 1-2 leisure items: pool/beach time, sunset cocktails, lounge time
- On-site dining for breakfast, lunch, AND dinner
- Use REAL amenity names if you know them ("Spa at Six Senses",
  "AyurMa Spa", "Blu Beach Club") not just "spa visit"
` : isResortProperty ? `
🏨 RESORT/PROPERTY-FOCUSED TRIP
The user specifically chose "${hotelName}" — they want to EXPERIENCE this property.
USE YOUR KNOWLEDGE of what this property offers.

PRIORITY FOR EACH DAY:
1. FIRST: Include 3-4 ON-PROPERTY activities per day (e.g., spa treatments, pool/beach time,
   wine tasting at vineyard, resort restaurants, wellness programs, cooking classes,
   golf, tennis, kids club, guided nature walks, sunset cocktails at the bar, etc.)
   - Use the REAL amenity names if you know them (e.g., "Spa at Six Senses" not just "spa visit")
   - Schedule relaxation time — resort guests don't want every minute planned
2. THEN (optional): At MOST 1 off-property activity nearby (local attraction, village, market)
   — and only if it's within 30 min of the resort. NEVER multiple off-property items per day.
3. AT LEAST ONE full "resort day" per stay with NO off-property activities

MEALS: Prioritize the resort's OWN restaurants (use real names if known).
At most 1 off-property meal per stay.
` : ''}
DAILY PLANNING RULES:
1. MOST DAYS: Activities within 30-60 min of hotel
   - Breakfast near hotel or at hotel
   - Activities in the local area
   - Dinner near hotel

2. DAY TRIPS (1-2 per trip max): Can go 1-2 hours away for must-see attractions
   USE YOUR KNOWLEDGE to identify appropriate day trips from ${hotelAddress || hotelName || 'this location'}:
   - What famous attractions, towns, or natural wonders are 1-2 hours away?
   - Examples: Geneva→Gruyères, Paris→Giverny, Lisbon→Sintra, Cusco→Machu Picchu, Munich→Neuschwanstein
   - Have lunch at day trip destination, return for dinner near hotel
   - Label these clearly as "Day Trip to [Place]"

3. ❌ NEVER DO:
   - Multiple distant locations on SAME day (e.g., morning Ubud + afternoon Seminyak + dinner Uluwatu = too much driving)
   - Activities in completely different regions on same day
   - Restaurants hours away from where that day's activities are

4. LOGICAL FLOW:
   - Each day's activities should be clustered in ONE area
   - If doing a day trip, ALL that day's activities are at the day trip destination
   - Don't zigzag across regions
`;
  } else if (effectiveLocation.includes(',') || effectiveLocation.includes('(')) {
    // Regional destination like "Costa Rica (Guanacaste Province)" or "Costa Rica, Guanacaste"
    const parts = effectiveLocation.split(/[,()]/).map(p => p.trim()).filter(p => p);
    const country = parts[0] || '';
    const region = parts[1] || '';

    geographicConstraints = `
📍 CRITICAL: REGIONAL TRIP TO ${region.toUpperCase() || country.toUpperCase()}
The user selected: ${effectiveLocation}

⚠️ THIS IS A REGIONAL TRIP - NOT A COUNTRY-WIDE TOUR
The user wants to explore ${region || country}, NOT the entire country of ${country}.

USE YOUR GEOGRAPHIC KNOWLEDGE to plan this trip properly:

STEP 1: PICK A BASE LOCATION
- Identify the main town(s) or area(s) within ${region || effectiveLocation}
- Pick ONE as the base for the entire trip
- The hotel should be in this base area
- Return to the same hotel each night

STEP 2: CLUSTER ACTIVITIES
- ALL activities should be within 30-60 min of the base
- Restaurants should be near activities or near the hotel
- USE YOUR KNOWLEDGE: What attractions, beaches, parks, towns are actually IN or NEAR ${region}?

STEP 3: DAY TRIPS (1-2 per trip max)
- What famous attractions are 1-2 hours from your chosen base?
- Examples of good day trips: nearby national parks, historic towns, scenic viewpoints
- Have lunch at day trip destination, return for dinner near hotel

STEP 4: WHAT TO AVOID
❌ DO NOT scatter activities across different regions of ${country}
❌ No activities that would require 3+ hours of driving
❌ No zigzagging between distant areas on the same day
❌ No restaurants that are hours away from activities

THINK ABOUT ACTUAL GEOGRAPHY:
- How big is ${region}? What are the main areas within it?
- What OTHER regions of ${country} should be AVOIDED because they're too far?
- If you're unsure about distances, err on the side of keeping activities closer together

Remember: The user chose ${region || 'this specific region'} for a reason. Keep the trip focused there.
`;
  }

  // Build per-day safari/wellness context if detected
  let perDaySafariRules = '';
  if (isSafariContext && tripType !== 'hotel') {
    perDaySafariRules = `
🦁 SAFARI CONTEXT: This stop is in a safari/wildlife area.
- Morning game drive (05:30-09:00), evening game drive (16:00-19:00)
- Midday: lighter activities (pool, spa, lodge relaxation) — animals hide during heat
- Include sundowner drinks, bush walks, night drives where applicable
- Do NOT schedule strenuous activities during midday heat
`;
  }

  const prompt = `Generate ${batchSize > 1 ? `Days ${dayNumber}-${batchEndDay}` : `Day ${dayNumber}`} of a ${totalDays}-day trip to ${tripDescription}.
${vagueLocationInstructions}
${geographicConstraints}
${resortGeoConstraint}
${perDaySafariRules}
${avoidList}
${varietyHint}
${recentDayTripsWarning}
${travelInstructions}
${stopInstructions}
${transportInstructions}
${feedbackInstructions}

SCHEDULING RULES (MUST FOLLOW):
1. No two activities at the same time unless at the same venue
2. Allow travel buffer between locations: same area 15-20 min, different areas 30-45 min
3. Meal times for ${effectiveLocation}:
   - Breakfast: ${regionalMealTimes.breakfast[0]}:00-${regionalMealTimes.breakfast[1]}:00
   - Lunch: ${regionalMealTimes.lunch[0]}:00-${regionalMealTimes.lunch[1]}:00
   - Dinner: ${regionalMealTimes.dinner[0]}:00-${regionalMealTimes.dinner[1]}:00
${travelInfo ? `4. TRAVEL DAY: No activities during transit window. First activity 2-3 hours after arrival.` : ''}
${dayNumber === 1 ? `4. ARRIVAL DAY: The traveler arrives at ${userPrefs.arrivalTime || '12:00 (noon)'}. NO activities before arrival. First activity no earlier than 2-3 hours after ${userPrefs.arrivalTime || '12:00'} (time needed for customs, baggage, transit to hotel, check-in). ${!userPrefs.arrivalTime ? 'Arrival time not specified - assuming midday arrival, so first activity around 14:00-15:00.' : ''}
   🍽️ ARRIVAL DAY MEALS (MANDATORY): You MUST include dinner on arrival day - the traveler needs to eat! Even if arriving late evening, include at minimum one dinner recommendation at a nearby restaurant. If arriving afternoon, include both lunch and dinner. NEVER skip dinner on Day 1.` : ''}
${isLastDay && userPrefs.departureTime ? `
5. 🚨 CRITICAL - THIS IS THE DEPARTURE DAY:
   - Flight departs at ${userPrefs.departureTime} from ${userPrefs.departureAirport || 'departure airport'}${isRedEyeDeparture ? `
   - ⚠️ RED-EYE DEPARTURE: This is a red-eye flight departing before 3 AM. The traveler must leave for the airport the EVENING BEFORE (Day ${dayNumber - 1}). Do NOT schedule any evening activities on Day ${dayNumber - 1}. Day ${dayNumber} should ONLY contain: transit to airport → departure.` : `
   - Traveler must LEAVE FOR AIRPORT by ${leaveByTime} (3 hours before departure for international flights)
   - ALL activities must END before ${leaveByTime}
   - If ${leaveByTime} is before 09:00, this is an EARLY DEPARTURE — schedule ONLY:
     * Early breakfast at hotel or grab-and-go café (30 min, starting at 06:00-06:30)
     * Hotel checkout immediately after breakfast
     * Transit/drive to airport
     * NO sightseeing, NO day trips, NO tours, NO walking tours
   - If ${leaveByTime} is between 09:00 and 12:00, you may include 1 brief morning activity near the hotel`}
   - DAY STRUCTURE: Light breakfast → Hotel Checkout → Transit to Airport → Departure
   - Do NOT schedule city tours, day trips, or activities far from the airport
   - Do NOT schedule ANY activity that starts after ${isRedEyeDeparture ? '18:00 on the previous day' : leaveByTime}
   - If the departure airport is in a different city from the hotel, the ONLY reason to go to that city is to catch the flight — NOT for sightseeing
   - 🚆 AIRPORT TRANSIT MODE: ${preFetchedTransport?._departureLeg?.mode
     ? `Take the ${preFetchedTransport._departureLeg.mode === 'train' ? 'train' : preFetchedTransport._departureLeg.mode} to the airport (${preFetchedTransport._departureLeg.duration}). Use this EXACT mode — do not suggest an alternative.`
     : 'Use the most practical ground transport to reach the airport. For nearby cities (under 3 hours apart), ALWAYS prefer high-speed train (Shinkansen, TGV, AVE, KTX, etc.) or regular train over flying. Do NOT suggest a short-haul domestic flight between nearby cities. Only suggest a connecting flight if the airport is on a different island or more than 4+ hours away by ground.'}
   - Keep it simple: breakfast, checkout, drive/transit to airport, depart` : ''}
${isLastDay && !userPrefs.departureTime ? `5. DEPARTURE DAY: Departure time not specified - assume afternoon departure. Include morning activities and checkout, last activity by 12:00.` : ''}

User preferences:
- Budget: ${userPrefs.budget || 'moderate'}
${(userPrefs.budget || '').toLowerCase() === 'budget' ? `💰 BUDGET TIER: Suggest hostels/guesthouses ($50-100/night). Prioritize street food, markets, local cafes. Free walking tours, parks, free museums. Include approximate costs in descriptions. Budget buses/public transit over taxis.` : (userPrefs.budget || '').toLowerCase() === 'luxury' ? `💎 LUXURY TIER: Suggest 5-star hotels/luxury resorts ($250+/night). Fine dining, Michelin-starred restaurants. Private guided tours, VIP access. Private transfers over public transit.` : ''}
- Activity level: ${userPrefs.activityLevel || 'moderate'}
- Pace: ${userPrefs.preferredPace || 'moderate'}
- Cuisine preferences: ${userPrefs.cuisinePreferences?.join(', ') || 'flexible'}
${userPrefs._tripContextBlock || ''}
${userPrefs.interests?.length > 0 ? `\n🎯 TRIP INTERESTS (HIGH PRIORITY - these are what the user specifically asked for):\n${userPrefs.interests.map(i => `- ${i}`).join('\n')}\nSchedule activities that match these interests. At least 1-2 activities per day should relate to these interests.\n` : ''}
${userPrefs.requestedPlaces?.length > 0 ? `\n📍 SPECIFIC PLACES REQUESTED BY USER (MUST include these):\n${userPrefs.requestedPlaces.map(p => `- ${p}`).join('\n')}\nThese are specific venues/attractions the user explicitly asked for. Include them in the itinerary.\n` : ''}
${userPrefs.dreamDestinations?.length > 0 ? `\n🌟 DREAM DESTINATIONS: ${userPrefs.dreamDestinations.join(', ')}\n` : ''}${userPrefs.lovedPlaces?.length > 0 ? `\n❤️ PLACES THEY'VE LOVED: ${userPrefs.lovedPlaces.join(', ')} (use these to understand their taste)\n` : ''}${userPrefs.avoidPlaces?.length > 0 ? `\n🚫 PLACES/THINGS TO AVOID: ${userPrefs.avoidPlaces.join(', ')}\n` : ''}
${userPrefs.favoriteDestinations?.length > 0 ? `\n🌟 USER FAVORITE DESTINATIONS (incorporate if relevant to this trip):\n${userPrefs.favoriteDestinations.map(f => `- ${f}`).join('\n')}\nIf any of these match or overlap with the current destination, prioritize visiting those places and related experiences.\n` : ''}
${userPrefs.leastFavoriteDestinations?.length > 0 ? `\n👎 LEAST FAVORITE DESTINATIONS (the user does NOT enjoy these types of places):\n${userPrefs.leastFavoriteDestinations.map(f => `- ${f}`).join('\n')}\nAvoid suggesting similar experiences or destinations to these.\n` : ''}
${userPrefs.travelNotes ? `\n📝 SPECIAL REQUESTS FROM USER:\n${userPrefs.travelNotes}\nPlease honor these specific requests when planning the itinerary.\n` : ''}
${buildFeedbackContext(userPrefs)}
${userPrefs._communityContext || ''}
Return ONLY a JSON object ${batchSize > 1 ? `containing days ${dayNumber}-${batchEndDay}` : 'for this single day'} with this structure:
${batchSize > 1 ? `{
  "days": [` : ''}{
  "day": ${dayNumber},
  "date": "Day ${dayNumber}",
  "theme": "Theme for this day",
  "baseCity": "City name",
  "hotel": { "name": "Hotel Name", "address": "Full address", "pricePerNight": 150, "roomType": "Deluxe Double Room", "boardType": "Breakfast Included" },
  "schedule": [
    {
      "time": "09:00",
      "emoji": "🏛️",
      "activity": "Activity name",
      "location": "Full location with city, country",
      "address": "Full street address",
      "description": "Detailed description with insider tips",
      "duration": "2 hours",
      "type": "sightseeing",
      "priceTier": "$$",
      "whatToDo": [
        { "title": "Step 1", "description": "What to do first", "duration": "30 min" },
        { "title": "Step 2", "description": "Next step", "duration": "45 min" }
      ],
      "tips": [
        { "type": "tip", "title": "Pro tip", "content": "Helpful advice", "icon": "💡" }
      ]
    }
  ],
  "meals": [
    {
      "time": "08:00",
      "emoji": "🥐",
      "meal": "Breakfast",
      "restaurant": "Restaurant Name",
      "cuisine": "Cuisine type",
      "priceRange": "$$",
      "address": "Full address",
      "mustTry": ["Signature dish 1", "Signature dish 2"],
      "specialty": "What they're known for"
    }
  ],
  "freeTimeSuggestions": [
    { "emoji": "☕", "activity": "Specific place name", "description": "Brief description", "timeOfDay": "morning|afternoon|evening|any" }
  ]
}${batchSize > 1 ? `
  // ... repeat for each day ${dayNumber} through ${batchEndDay}, each with unique activities
  ]
}` : ''}

Include 3-5 activities and 3 meals (breakfast, lunch, dinner) PER DAY.${batchSize > 1 ? `
🔢 MULTI-DAY BATCH: Generate ${batchSize} complete days (Day ${dayNumber} through Day ${batchEndDay}). Each day must be a separate object in the "days" array. Each day needs its own unique activities, meals, and hotel. Do NOT repeat activities between days.` : ''}
CRITICAL: ALL times MUST be in 24-hour format (HH:MM). Use "13:00" not "1:00", "19:00" not "7:00", "14:30" not "2:30". Morning times: "08:00"-"11:59". Afternoon: "12:00"-"17:59". Evening: "18:00"-"23:59". NEVER output bare single/double digit times like "1:00" or "7:00" for afternoon/evening — always use 24-hour format.
IMPORTANT: All meals (breakfast, lunch, dinner) belong ONLY in the "meals" array. Do NOT also add them as schedule activities - this creates duplicates. For example, do NOT put "Lunch at Restaurant X" in schedule AND "Restaurant X" in meals.
${dayNumber === 1 ? `⚠️ ARRIVAL DAY MEALS - THIS IS MANDATORY: Even though the traveler arrives later, you MUST include dinner in the meals array. If arriving in the afternoon, include lunch AND dinner. Dinner is the MOST IMPORTANT meal on arrival day. Schedule dinner at a well-known local restaurant between ${regionalMealTimes.dinner[0]}:00-${regionalMealTimes.dinner[1]}:00. DO NOT return a Day 1 without dinner - this is a hard requirement.` : ''}
IMPORTANT: Each activity MUST include "whatToDo" array with 2-4 specific steps to maximize the experience.
IMPORTANT: Each activity and meal MUST have an appropriate "emoji" field (e.g., 🏛️ for museums, 🚂 for trains, 🍝 for Italian food). Use context - "Interlaken" is a city, not a lake!
CRITICAL FOR TRANSIT CALCULATIONS:
- "address" field MUST be a REAL, SPECIFIC street address that Google Maps can find (e.g., "1600 Pennsylvania Avenue NW, Washington DC" not just "White House")
- "location" field MUST include city and country (e.g., "Red Sand Dunes, Riyadh Province, Saudi Arabia" not just "Red Sand Dunes")
- NEVER use generic names like "Sunset Viewpoint", "City Center", "Morning in X", "Cultural Morning", "Explore X" - use the actual place name with full address
- For attractions without street addresses, use the nearest landmark with GPS-searchable location

❌ BANNED GENERIC ACTIVITIES - NEVER USE THESE:
- "Explore Nearby Streets" or "Explore the area" or "Wander around"
- "Free time" or "Leisure time" or "Rest at hotel"
- "City walking tour" (without a specific route)
- Any activity that could apply to ANY city in the world
- Instead, use SPECIFIC, REAL places: named temples, specific markets, actual parks, real museums, famous streets by name

⏰ ACTIVITY TIME-KEYWORD RULES (BUG 17 FIX):
Activities with time-specific keywords MUST be scheduled at the correct time:
- "dawn", "sunrise", "early morning" → schedule BEFORE 06:30
- "morning game drive" → 05:30-06:00 start
- "sunset", "sundowner", "golden hour" → schedule AFTER 16:00
- "afternoon game drive" → 15:30-16:00 start
- "night drive", "nocturnal" → schedule AFTER 20:00
- "brunch" → 09:30-11:00
- Never schedule a "dawn" activity at 09:00 — that is mid-morning, NOT dawn
${isLastDay ? 'This is the LAST day - include departure logistics (checkout, airport transfer) as SCHEDULE activities. The hotel field MUST still be the actual hotel name — NEVER put "Departure to..." or "Flight to..." as the hotel name.' : ''}
`;

  // Inject skeleton locks for this day's range if available.
  // Same guard as buildFirstBatchUserMessage: only inject when skeleton
  // expansion produced more cities than the input had stops.
  const inputStops = travelStruct?.stops || [];
  const skeletonExpandedBeyondInput = (skeleton?.dayPlan?.length || 0) > 0 &&
    new Set((skeleton?.dayPlan || []).map(d => (d.baseCity || '').toLowerCase())).size > inputStops.length;
  const lockEnd = batchEndDay || dayNumber;
  const skeletonLocks = skeletonExpandedBeyondInput
    ? buildSkeletonLockText(skeleton, dayNumber, lockEnd)
    : '';
  return skeletonLocks ? skeletonLocks + prompt : prompt;
}
